import React, { useRef } from 'react';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { LayoutDashboard, ShoppingBag, Bell, ChefHat, Smartphone, BarChart3 } from 'lucide-react';

const kpis = [
    { label: 'Faturamento hoje', value: 'R$ 8.420', delta: '+18%' },
    { label: 'Pedidos ativos', value: '32', delta: '08 na cozinha' },
    { label: 'Reservas', value: '14', delta: '03 chegando' },
];

const dashboardBars = [42, 68, 56, 84, 74, 91, 63];

const recentOrders = [
    { id: '#4021', customer: 'Mesa 04', status: 'Em preparo', total: 'R$ 128' },
    { id: '#4022', customer: 'Delivery Juliana', status: 'Saiu para entrega', total: 'R$ 74' },
    { id: '#4023', customer: 'Retirada Carlos', status: 'Pronto', total: 'R$ 52' },
];

const StickyScrollExperience: React.FC = () => {
    const containerRef = useRef<HTMLDivElement>(null);
    const { scrollYProgress } = useScroll({
        target: containerRef,
        offset: ['start start', 'end end'],
    });

    const smoothProgress = useSpring(scrollYProgress, {
        stiffness: 100,
        damping: 30,
        restDelta: 0.001
    });

    // Scene 1: Storytelling text
    const storyOpacity = useTransform(smoothProgress, [0, 0.1, 0.2, 0.25], [0, 1, 1, 0]);
    const storyScale = useTransform(smoothProgress, [0, 0.1, 0.2], [0.8, 1, 1.1]);
    const storyY = useTransform(smoothProgress, [0, 0.2], [50, -50]);

    // Scene 2: Dashboard Preview
    // It starts appearing at 0.25, full at 0.4, finishes at 0.7
    const dashOpacity = useTransform(smoothProgress, [0.25, 0.35, 0.65, 0.75], [0, 1, 1, 0]);
    const dashScale = useTransform(smoothProgress, [0.25, 0.4, 0.6], [0.7, 1, 1.05]);
    const dashRotateX = useTransform(smoothProgress, [0.25, 0.4], [25, 0]);
    const dashY = useTransform(smoothProgress, [0.25, 0.6], [100, -50]);

    // Scene 3: Feature Highlights (The "Gaps")
    const feature1Opacity = useTransform(smoothProgress, [0.45, 0.5, 0.55, 0.6], [0, 1, 1, 0]);
    const feature2Opacity = useTransform(smoothProgress, [0.6, 0.65, 0.7, 0.75], [0, 1, 1, 0]);

    return (
        <div ref={containerRef} className="relative h-[400vh] bg-background">
            <div className="sticky top-0 flex h-screen w-full items-center justify-center overflow-hidden">
                
                {/* Background Ambient Glow */}
                <motion.div 
                    style={{ opacity: useTransform(smoothProgress, [0, 0.5, 1], [0.3, 0.6, 0.3]) }}
                    className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(249,115,22,0.15),transparent_70%)]" 
                />

                {/* SCENE 1: The Problem */}
                <motion.div
                    style={{ opacity: storyOpacity, scale: storyScale, y: storyY }}
                    className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center"
                >
                    <span className="mb-4 text-xs font-bold uppercase tracking-[0.4em] text-primary">A Nova Era da Gestão</span>
                    <h2 className="max-w-4xl text-5xl font-black tracking-tighter text-white md:text-8xl">
                        Gerir um restaurante não deveria ser <br />
                        <span className="bg-gradient-to-r from-white/40 to-white bg-clip-text text-transparent italic">um exercício de caos.</span>
                    </h2>
                    <p className="mt-8 max-w-2xl text-xl font-medium leading-relaxed text-secondary md:text-2xl">
                        O <span className="text-white">Garçom Digital</span> sincroniza sua operação com o padrão máximo de qualidade.
                    </p>
                </motion.div>

                {/* SCENE 2: The Dashboard */}
                <motion.div
                    style={{ 
                        opacity: dashOpacity, 
                        scale: dashScale, 
                        rotateX: dashRotateX,
                        y: dashY
                    }}
                    className="relative w-full max-w-[1200px] px-5"
                >
                    <div className="overflow-hidden rounded-[32px] border border-white/10 bg-[#0e1014] shadow-[0_40px_150px_rgba(0,0,0,0.6)]">
                        {/* Browser Header */}
                        <div className="flex h-12 items-center justify-between border-b border-white/6 bg-white/[0.03] px-6">
                            <div className="flex gap-2">
                                <div className="h-2.5 w-2.5 rounded-full bg-[#ff5f56]/80" />
                                <div className="h-2.5 w-2.5 rounded-full bg-[#ffbd2e]/80" />
                                <div className="h-2.5 w-2.5 rounded-full bg-[#27c93f]/80" />
                            </div>
                            <div className="text-[11px] font-medium tracking-widest text-white/30 uppercase">Dashboard Inteligente</div>
                            <Bell className="h-4 w-4 text-white/40" />
                        </div>

                        <div className="grid gap-0 lg:grid-cols-[200px_1fr]">
                            <aside className="hidden border-r border-white/6 p-6 lg:block">
                                <div className="mb-8 flex items-center gap-3">
                                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/20 text-primary">
                                        <LayoutDashboard size={20} />
                                    </div>
                                    <span className="font-bold text-white">Gastroflow</span>
                                </div>
                                <div className="space-y-4">
                                    {['Visão Geral', 'Pedidos', 'Mesas', 'Menu', 'Equipe'].map((item, i) => (
                                        <div key={item} className={`text-sm ${i === 0 ? 'text-primary font-bold' : 'text-white/40'}`}>
                                            {item}
                                        </div>
                                    ))}
                                </div>
                            </aside>

                            <main className="p-6 md:p-8">
                                <div className="mb-8 flex flex-col justify-between gap-4 md:flex-row md:items-end">
                                    <div>
                                        <h3 className="text-2xl font-bold text-white">Performance do Dia</h3>
                                        <p className="text-sm text-white/40">Dados consolidados em tempo real</p>
                                    </div>
                                    <div className="flex gap-2">
                                        <div className="rounded-full bg-emerald-500/10 px-3 py-1.5 text-[10px] font-bold text-emerald-400 uppercase tracking-wider">Online</div>
                                    </div>
                                </div>

                                <div className="grid gap-4 md:grid-cols-3">
                                    {kpis.map((kpi) => (
                                        <div key={kpi.label} className="rounded-2xl border border-white/5 bg-white/[0.02] p-5">
                                            <p className="text-[10px] font-bold uppercase tracking-widest text-white/30">{kpi.label}</p>
                                            <div className="mt-3 flex items-baseline justify-between">
                                                <span className="text-xl font-bold text-white">{kpi.value}</span>
                                                <span className="text-xs text-primary">{kpi.delta}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="mt-6 grid gap-6 xl:grid-cols-2">
                                    <div className="rounded-2xl border border-white/5 bg-white/[0.02] p-6">
                                        <div className="mb-6 flex items-center justify-between">
                                            <p className="text-sm font-bold text-white">Fluxo de Pedidos</p>
                                            <ShoppingBag size={16} className="text-primary" />
                                        </div>
                                        <div className="space-y-4">
                                            {recentOrders.map(order => (
                                                <div key={order.id} className="flex items-center justify-between border-b border-white/5 pb-3 last:border-0 last:pb-0">
                                                    <div>
                                                        <p className="text-sm font-medium text-white">{order.customer}</p>
                                                        <p className="text-[10px] text-white/30 uppercase">{order.status}</p>
                                                    </div>
                                                    <span className="text-sm font-bold text-white">{order.total}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="rounded-2xl border border-white/5 bg-white/[0.02] p-6">
                                        <div className="mb-6 flex items-center justify-between">
                                            <p className="text-sm font-bold text-white">Receita Semanal</p>
                                            <BarChart3 size={16} className="text-white/40" />
                                        </div>
                                        <div className="flex h-32 items-end gap-2">
                                            {dashboardBars.map((height, i) => (
                                                <div key={i} className="flex-1 rounded-t-lg bg-gradient-to-t from-primary/40 to-primary/80" style={{ height: `${height}%` }} />
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </main>
                        </div>
                    </div>

                    {/* Overlay Feature 1: Intelligence */}
                    <motion.div 
                        style={{ opacity: feature1Opacity, scale: useTransform(smoothProgress, [0.45, 0.5], [0.8, 1]) }}
                        className="absolute -right-12 top-1/4 hidden w-72 rounded-3xl border border-primary/20 bg-black/80 p-6 backdrop-blur-2xl xl:block"
                    >
                        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary text-white shadow-glow-sm">
                            <ChefHat />
                        </div>
                        <h4 className="text-lg font-bold text-white">Gestão Preditiva</h4>
                        <p className="mt-2 text-sm text-white/60 leading-relaxed">
                            O sistema antecipa picos de movimento e sugere otimizações no estoque de forma automática.
                        </p>
                    </motion.div>

                    {/* Overlay Feature 2: Mobile Sync */}
                    <motion.div 
                        style={{ opacity: feature2Opacity, scale: useTransform(smoothProgress, [0.6, 0.65], [0.8, 1]) }}
                        className="absolute -left-12 bottom-1/4 hidden w-72 rounded-3xl border border-white/10 bg-black/80 p-6 backdrop-blur-2xl xl:block"
                    >
                        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-black">
                            <Smartphone />
                        </div>
                        <h4 className="text-lg font-bold text-white">Sincronia Total</h4>
                        <p className="mt-2 text-sm text-white/60 leading-relaxed">
                            Toda a equipe conectada em tempo real. Do pedido via QR Code à entrega na mesa.
                        </p>
                    </motion.div>
                </motion.div>

                {/* SCENE 3: Final Transition Hook */}
                <motion.div
                    style={{ 
                        opacity: useTransform(smoothProgress, [0.8, 0.9, 1], [0, 1, 0]),
                        scale: useTransform(smoothProgress, [0.8, 0.9], [1.2, 1])
                    }}
                    className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center"
                >
                    <h2 className="text-6xl font-black tracking-tighter text-white md:text-8xl">
                        A simplicidade <br />
                        <span className="text-primary italic">que vicia.</span>
                    </h2>
                </motion.div>
            </div>
        </div>
    );
};

export default StickyScrollExperience;
